// @flow strict

import React, { useEffect, useRef, useState } from "react";
import {
  BsArrowRepeat,
  BsChevronDown,
  BsChevronUp,
  BsFiletypeCsv,
  BsFiletypeXls,
  BsPlus,
  BsThreeDotsVertical,
} from "react-icons/bs";
import { Link } from "react-router-dom";
import { useCategoryStore, useServiceStore } from "../../store/serviceStore";
import AddServiceModal from "../../components/admin/AddServiceModal";
import EditServiceModal from "../../components/admin/EditServiceModal";
import { IoIosLogIn } from "react-icons/io";
import { formatDateForExport } from "../../functions/exportsFunctions";
import { FiX } from "react-icons/fi";
import { BiSearch } from "react-icons/bi";
import { MdOutlineFilterAlt, MdOutlineFilterAltOff } from "react-icons/md";
import { TbSwitchVertical } from "react-icons/tb";
import AddCategoryModal from "../../components/admin/AddCategoryModal";
import EditCategoryModal from "../../components/admin/EditCategoryModal";

const CataloguePage = () => {
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [exportLoading, setExportLoading] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    search: "",
  });
  const searchInputRef = useRef(null);

  const [sortOrder, setSortOrder] = useState(""); // 'name-asc', 'name-desc', 'price-asc', 'price-desc'
  const [showSortItems, setShowSortItems] = useState(false);

  const [addModal, setAddModal] = useState(false);
  const [isEditModal, setIsEditModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const [addCategoryModal, setAddCategoryModal] = useState(false);
  const [isEditCategoryModal, setIsEditCategoryModal] = useState(false);
  const [selectedActionCategory, setSelectedActionCategory] = useState(null);
  const [activeCategoryDropdown, setActiveCategoryDropdown] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState("all");
  const [activeDropdown, setActiveDropdown] = useState(null);

  const {
    servicesGroupByCategory,
    isLoadingServicesGroupByCategory,
    getServicesGroupByCategorie,
    changeStatusServiceLoading,
    changeStatusService,
  } = useServiceStore();
  const { changeStatusCategoryLoading, changeCategoryStatus } =
    useCategoryStore();

  useEffect(() => {
    getServicesGroupByCategorie();
  }, []);

  // Vérifier si servicesGroupByCategory est défini
  const isDataLoaded =
    servicesGroupByCategory && typeof servicesGroupByCategory === "object";

  // Calcul du nombre total de services
  const getTotalServicesCount = () => {
    if (!isDataLoaded) return 0;

    return Object.values(servicesGroupByCategory).reduce(
      (total, categoryData) => {
        return total + (categoryData?.services?.length || 0);
      },
      0
    );
  };

  // Gestion de l'activation/désactivation de la catégorie
  const handleToggleCategoryStatus = async (category) => {
    console.log("Changer statut du catégorie:", category);
    try {
      await changeCategoryStatus(category);
      setActiveCategoryDropdown(null);
    } catch (error) {
      setActiveCategoryDropdown(null);
    }
  };

  // Gestion de la modification de la catégorie
  const handleEditCategory = (category) => {
    console.log("Modifier le category:", category);
    setActiveCategoryDropdown(null);

    // selectedActionCategory
    // setSelectedActionCategory
    setSelectedActionCategory(category);
    setIsEditCategoryModal(true);
  };

  // Gestion de l'activation/désactivation d'un service
  const handleToggleServiceStatus = async (service) => {
    console.log("Changer statut du service:", service);
    try {
      await changeStatusService(service);
      setActiveDropdown(null);
    } catch (error) {
      setActiveDropdown(null);
    }
  };

  // Gestion de la modification d'un service
  const handleEditService = (service) => {
    // console.log("Modifier le service:", service);
    setActiveDropdown(null);
    setSelectedService(service);
    setIsEditModal(true);
  };

  // Fonction de tri des services
  const sortServices = (services) => {
    if (!services) return [];

    return [...services].sort((a, b) => {
      switch (sortOrder) {
        case "name-asc":
          return a.nom?.localeCompare(b.nom) || 0;
        case "name-desc":
          return b.nom?.localeCompare(a.nom) || 0;
        case "price-asc":
          return parseFloat(a.price || 0) - parseFloat(b.price || 0);
        case "price-desc":
          return parseFloat(b.price || 0) - parseFloat(a.price || 0);
        default:
          return 0;
      }
    });
  };

  // Fonction de filtrage des services
  const filterServices = (services) => {
    if (!services) return [];

    return services.filter((service) => {
      // Filtre par recherche
      const searchMatch =
        !filters.search ||
        service.nom?.toLowerCase().includes(filters.search.toLowerCase()) ||
        service.description
          ?.toLowerCase()
          .includes(filters.search.toLowerCase());

      // Filtre par statut
      const statusMatch =
        !filters.status ||
        (filters.status === "active" && service.is_active) ||
        (filters.status === "inactive" && !service.is_active);

      return searchMatch && statusMatch;
    });
  };

  // Obtenir les services groupés par catégorie pour l'affichage
  const getServicesGroupedByCategory = () => {
    if (!isDataLoaded) return [];

    if (selectedCategory === "all") {
      // Retourner toutes les catégories avec leurs services filtrés et triés
      return Object.entries(servicesGroupByCategory)
        .map(([categoryName, categoryData]) => ({
          category: categoryData.category,
          services: sortServices(filterServices(categoryData.services || [])),
        }))
        .filter((group) => group.services.length > 0); // Ne montrer que les catégories avec des services après filtrage
    } else {
      // Retourner seulement la catégorie sélectionnée avec services filtrés et triés
      const categoryData = servicesGroupByCategory[selectedCategory];
      if (!categoryData) return [];

      const filteredServices = filterServices(categoryData.services || []);
      if (filteredServices.length === 0) return [];

      return [
        {
          category: categoryData.category,
          services: sortServices(filteredServices),
        },
      ];
    }
  };

  // Obtenir le nombre total de services affichés (après filtrage)
  const getDisplayedServicesCount = () => {
    return getServicesGroupedByCategory().reduce(
      (total, group) => total + group.services.length,
      0
    );
  };

  // Gestion des filtres
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      status: "",
      search: "",
    });

    setSortOrder("");
    setShowFilters(false);
    setShowMobileFilters(false);
  };

  const applyFilters = () => {
    setShowMobileFilters(false);
  };

  // Gestion du tri
  const handleSortChange = (newSortOrder) => {
    setShowSortItems(false);
    setSortOrder(newSortOrder);
  };

  const handleExport = (type) => {
    // Votre code d'export existant
  };

  const handleImport = () => {
    console.log("Importer des services");
  };

  const handleMerge = () => {
    console.log("Fusionner des services");
  };

  if (isLoadingServicesGroupByCategory) {
    return (
      <div className="fixed inset-0 flex justify-center items-center z-50">
        <div className="animate-spin rounded-full h-5 w-5 border-t-4 mr-4 border-b-4 border-secondary" />
        <span className="font-nunito text-[14px] text-text">Chargement...</span>
      </div>
    );
  }

  const groupedServices = getServicesGroupedByCategory();

  // Modal des filtres pour mobile
  const MobileFiltersModal = () => (
    <div
      className={`fixed top-0 left-0 h-full w-4/5 max-w-sm bg-white z-50 shadow-xl transform transition-transform lg:hidden
      ${showMobileFilters ? "translate-x-0" : "-translate-x-full"}`}
    >
      <div className="p-4 h-full overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-poppins font-semibold text-[18px] text-title">Filtres et Tri </h2>
          <button
            onClick={() => setShowMobileFilters(false)}
            className="text-gray-500 hover:text-gray-700 p-1"
          >
            <FiX size={24} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Filtre Statut */}
          <div>
            <label className="font-poppins font-medium text-[15px] text-subtitle block mb-2">
              Statut
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="w-full border border-gray-300 px-3 py-2 font-nunito text-[14px] text-text focus:outline-none focus:ring-2 focus:ring-secondary-500"
            >
              <option value="">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </div>

          {/* Tri */}
          <div>
            <label className="font-poppins font-medium text-[15px] text-subtitle block mb-2">
              Trier par
            </label>
            <select
              value={sortOrder}
              onChange={(e) => handleSortChange(e.target.value)}
              className="w-full border border-gray-300 px-3 py-2 font-nunito text-[14px] text-text focus:outline-none focus:ring-2 focus:ring-secondary-500"
            >
              <option value="" disabled>
                Gérer l'ordre
              </option>
              <option value="name-asc">Nom (A-Z)</option>
              <option value="name-desc">Nom (Z-A)</option>
              <option value="price-asc">Prix (Croissant)</option>
              <option value="price-desc">Prix (Décroissant)</option>
            </select>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
          <div className="flex gap-3">
            <button
              onClick={resetFilters}
              className="flex-1 px-4 py-2 font-nunito text-[14px] text-text border border-gray-300 hover:bg-gray-50"
            >
              Réinitialiser
            </button>
            <button
              onClick={applyFilters}
              className="flex-1 px-4 py-2 bg-secondary-600 text-white font-nunito text-[14px] hover:bg-secondary-700"
            >
              Appliquer
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="">
      {/* Overlay pour mobile */}
      {showMobileFilters && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setShowMobileFilters(false)}
        />
      )}

      <MobileFiltersModal />

      {/* En-tête principal */}
      <div className="flex flex-col lg:flex-row gap-2 w-full lg:justify-between lg:items-center mb-8">
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
            <h1 className="font-poppins font-semibold text-[18px] text-title mb-2">
              Catalogue de prestations
            </h1>
            <span className="bg-gray-100 px-3 py-1 font-nunito text-[14px] text-text w-fit">
              {getDisplayedServicesCount()} prestation
              {getDisplayedServicesCount() !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-gray-600">
            <p className="font-nunito text-[14px] text-text">
              Consultez et gérez les prestations offertes par votre entreprise.
            </p>
            <Link to="" className="font-nunito font-semibold text-[14px] text-action hover:underline">
              En savoir plus
            </Link>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full justify-end lg:w-auto">
          {/* Dropdown de vue */}
          <div className="relative select-none" ref={dropdownRef}>
            <button
              className="flex items-center justify-between w-full sm:w-auto gap-2 py-2 px-4 border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
              title="Options"
              onClick={() => setShowViewDropdown(!showViewDropdown)}
              disabled={exportLoading}
            >
              <span className="font-nunito font-semibold text-[14px]">
                {exportLoading ? "Export..." : "Options"}
              </span>
              {exportLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
              ) : showViewDropdown ? (
                <BsChevronUp size={12} />
              ) : (
                <BsChevronDown size={12} />
              )}
            </button>

            {showViewDropdown && (
              <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 shadow-lg z-20 w-64">
                <div className="p-2">
                  <button
                    className="w-full text-left py-2 px-3 font-nunito text-[14px] text-text hover:bg-gray-50 flex items-center gap-2"
                    onClick={() => {
                      handleImport();
                      setShowViewDropdown(false);
                    }}
                  >
                    <IoIosLogIn size={17} className="text-gray-600" />
                    <span className="font-nunito font-semibold text-[14px]">Importer des services</span>
                  </button>
                  <button
                    className="w-full text-left py-2 px-3 font-nunito text-[14px] text-text hover:bg-gray-50 flex items-center gap-2"
                    onClick={() => {
                      handleMerge();
                      setShowViewDropdown(false);
                    }}
                  >
                    <BsArrowRepeat size={17} className="text-gray-600" />
                    <span className="font-nunito font-semibold text-[14px]">Fusionner des services</span>
                  </button>

                  <div className="border-t my-2"></div>

                  <div className="px-3 py-1 font-poppins font-medium text-[15px] text-subtitle uppercase tracking-wide">
                    Exporter
                  </div>

                  <button
                    className="w-full text-left py-2 px-3 font-nunito text-[14px] text-text hover:bg-gray-50 flex items-center gap-2"
                    onClick={() => {
                      handleExport("excel");
                      setShowViewDropdown(false);
                    }}
                  >
                    <BsFiletypeXls size={17} className="text-gray-600" />
                    <span className="font-nunito font-semibold text-[14px]">Excel</span>
                  </button>
                  <button
                    className="w-full text-left py-2 px-3 font-nunito text-[14px] text-text hover:bg-gray-50 flex items-center gap-2"
                    onClick={() => {
                      handleExport("csv");
                      setShowViewDropdown(false);
                    }}
                  >
                    <BsFiletypeCsv size={17} className="text-gray-600" />
                    <span className="font-nunito font-semibold text-[14px]">CSV</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => setAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-600 text-white hover:bg-secondary-700 transition-colors"
          >
            <BsPlus size={16} />
            <span className="font-nunito font-semibold text-[14px]">Ajouter</span>
          </button>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-gray-100 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Barre de recherche */}
          <div className="flex-1 relative">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <BiSearch size={20} className="text-gray-400" />
              </div>

              <input
                ref={searchInputRef}
                type="text"
                placeholder="Rechercher par nom ou description..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 font-nunito text-[14px] text-text focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
              />

              {filters.search && (
                <button
                  className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-200 px-2"
                  aria-label="Effacer la recherche"
                  onClick={() => handleFilterChange("search", "")}
                >
                  <FiX size={18} className="text-gray-400" />
                </button>
              )}
            </div>
          </div>

          {/* Boutons de filtre et création */}
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            {/* Bouton Filtres - Mobile */}
            <button
              className="flex md:hidden items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 font-nunito font-semibold text-[14px] hover:bg-gray-50 transition-colors"
              onClick={() => setShowMobileFilters(true)}
            >
              Filtres & Tri
              <MdOutlineFilterAlt size={17} />
            </button>

            {/* Bouton Filtres - Desktop */}
            <button
              className="hidden md:flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 font-nunito font-semibold text-[14px] hover:bg-gray-50 transition-colors"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? (
                <>
                  Cacher les filtres
                  <MdOutlineFilterAltOff size={17} />
                </>
              ) : (
                <>
                  Filtres & Tri
                  <MdOutlineFilterAlt size={17} />
                </>
              )}
            </button>

            {/* Menu déroulant pour le tri */}
            <div className="relative flex-1">
              <button
                className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 hover:bg-gray-100 transition-colors"
                onClick={() => setShowSortItems(true)}
              >
                <TbSwitchVertical size={12} />
                <span className="font-nunito font-semibold text-[14px]">
                  {sortOrder === "" && "Gérer l'ordre"}
                  {sortOrder === "name-asc" && "Nom (A-Z)"}
                  {sortOrder === "name-desc" && "Nom (Z-A)"}
                  {sortOrder === "price-asc" && "Prix (Croissant)"}
                  {sortOrder === "price-desc" && "Prix (Décroissant)"}
                </span>
                <BsChevronDown size={10} />
              </button>

              {/* Menu de tri */}
              {showSortItems && (
                <div className="absolute top-full right-0 mt-1 w-48 bg-white shadow-lg border border-gray-200 z-10 hidden md:block group-hover:block">
                  <button
                    onClick={() => handleSortChange("")}
                    className={`w-full text-left px-4 py-2 font-nunito text-[14px] text-text hover:bg-gray-100 ${
                      sortOrder === ""
                        ? "bg-secondary-50 text-secondary-700"
                        : "text-gray-700"
                    }`}
                  >
                    Gérer l'ordre
                  </button>

                  <button
                    onClick={() => handleSortChange("name-asc")}
                    className={`w-full text-left px-4 py-2 font-nunito text-[14px] text-text hover:bg-gray-100 ${
                      sortOrder === "name-asc"
                        ? "bg-secondary-50 text-secondary-700"
                        : "text-gray-700"
                    }`}
                  >
                    Nom (A-Z)
                  </button>

                  <button
                    onClick={() => handleSortChange("name-desc")}
                    className={`w-full text-left px-4 py-2 font-nunito text-[14px] text-text hover:bg-gray-100 ${
                      sortOrder === "name-desc"
                        ? "bg-secondary-50 text-secondary-700"
                        : "text-gray-700"
                    }`}
                  >
                    Nom (Z-A)
                  </button>

                  <button
                    onClick={() => handleSortChange("price-asc")}
                    className={`w-full text-left px-4 py-2 font-nunito text-[14px] text-text hover:bg-gray-100 ${
                      sortOrder === "price-asc"
                        ? "bg-secondary-50 text-secondary-700"
                        : "text-gray-700"
                    }`}
                  >
                    Prix (Croissant)
                  </button>

                  <button
                    onClick={() => handleSortChange("price-desc")}
                    className={`w-full text-left px-4 py-2 font-nunito text-[14px] text-text hover:bg-gray-100 ${
                      sortOrder === "price-desc"
                        ? "bg-secondary-50 text-secondary-700"
                        : "text-gray-700"
                    }`}
                  >
                    Prix (Décroissant)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filtres avancés (Desktop) */}
        {showFilters && (
          <div className="mt-4 p-4 bg-white border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="font-poppins font-medium text-[15px] text-subtitle block mb-1">
                  Statut
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2 font-nunito text-[14px] text-text focus:outline-none focus:ring-2 focus:ring-secondary-500"
                >
                  <option value="">Tous les statuts</option>
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                </select>
              </div>

              <div>
                <label className="font-poppins font-medium text-[15px] text-subtitle block mb-1">
                  Trier par
                </label>
                <select
                  value={sortOrder}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2 font-nunito text-[14px] text-text focus:outline-none focus:ring-2 focus:ring-secondary-500"
                >
                  <option value="" disabled>
                    Gérer l'ordre
                  </option>
                  <option value="name-asc">Nom (A-Z)</option>
                  <option value="name-desc">Nom (Z-A)</option>
                  <option value="price-asc">Prix (Croissant)</option>
                  <option value="price-desc">Prix (Décroissant)</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={resetFilters}
                className="px-4 py-2 font-nunito text-[14px] text-text hover:text-gray-800"
              >
                Réinitialiser
              </button>
              <button
                onClick={() => setShowFilters(false)}
                className="px-4 py-2 bg-secondary-600 text-white font-nunito text-[14px] hover:bg-secondary-700"
              >
                Appliquer
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Le reste de votre code pour l'affichage des services reste inchangé */}
      <div className="flex flex-col md:flex-row gap-4 relative">
        {/* Colonne gauche - Catégories */}
        <div className="md:w-1/3">
          <div className="sticky top-4">
            <div className="bg-white border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h2 className="font-poppins font-semibold text-[18px] text-title">
                  Catégories
                </h2>
              </div>
              <div className="p-3 ">
                <button
                  className={`w-full flex flex-shrink-0 flex-wrap justify-between items-center p-3 transition-colors ${
                    selectedCategory === "all"
                      ? "bg-secondary-50 border border-secondary-200"
                      : "hover:bg-gray-50"
                  }`}
                  onClick={() => setSelectedCategory("all")}
                >
                  <span className="font-poppins font-medium text-[15px] text-subtitle">
                    Toutes les catégories
                  </span>
                  <span className="bg-gray-100 text-gray-600 px-2 py-1 font-nunito text-[14px] font-medium min-w-8 text-center">
                    {getTotalServicesCount()}
                  </span>
                </button>

                {!isLoadingServicesGroupByCategory &&
                  Object.entries(servicesGroupByCategory).map(
                    ([categoryName, categoryData]) => (
                      <button
                        key={categoryData?.category?.id || categoryName}
                        className={`w-full flex justify-between items-center p-3 transition-colors ${
                          selectedCategory === categoryName
                            ? "bg-secondary-50 border border-secondary-200"
                            : "hover:bg-gray-50"
                        }`}
                        onClick={() => setSelectedCategory(categoryName)}
                      >
                        <span className="font-poppins font-medium text-[15px] text-subtitle">
                          {categoryName}
                        </span>
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 font-nunito text-[14px] font-medium min-w-8 text-center">
                          {categoryData?.services?.length || 0}
                        </span>
                      </button>
                    )
                  )}

                <div className="mt-4 pt-3 border-t border-gray-200">
                  <button
                    onClick={() => setAddCategoryModal(true)}
                    className="w-full flex items-center gap-2 p-3 font-nunito font-semibold text-[14px] text-action hover:bg-secondary-50 transition-colors"
                  >
                    <BsPlus size={18} />
                    <span>Ajouter une catégorie</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Colonne droite - Services */}
        <div className="md:w-2/3">
          <div>
            <div className="p-4">
              {groupedServices.length > 0 ? (
                groupedServices.map(({ category, services }) => (
                  <div
                    key={category?.id || category?.name}
                    className="mb-8 last:mb-0"
                  >
                    <div className="mb-4 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-poppins font-semibold text-[18px] text-title">
                            {category?.name}
                          </h3>
                          {!category.isActive && (
                            <span
                              className={`px-2 py-1 font-nunito text-[14px] font-medium ${
                                category.isActive
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {category.isActive ? "Actif" : "Désactivée"}
                            </span>
                          )}
                        </div>

                        <p className="font-nunito text-[14px] text-text">
                          {services.length} prestation
                          {services.length !== 1 ? "s" : ""}
                        </p>
                      </div>

                      <div className="relative">
                        <button
                          onClick={() =>
                            setActiveCategoryDropdown(
                              activeCategoryDropdown === category.id
                                ? null
                                : category.id
                            )
                          }
                          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 hover:bg-gray-100 transition-colors"
                        >
                          <span className="font-nunito font-semibold text-[14px]">Actions</span>
                          <BsChevronDown size={12} />
                        </button>

                        {activeCategoryDropdown === category.id && (
                          <div className="absolute right-0 top-10 mt-1 w-48 bg-white shadow-lg border border-gray-200 z-10">
                            <button
                              onClick={() => handleEditCategory(category)}
                              className="w-full text-left px-4 py-3 font-nunito text-[14px] text-text hover:bg-gray-100"
                            >
                              Modifier
                            </button>

                            <button
                              onClick={() =>
                                handleToggleCategoryStatus(category)
                              }
                              disabled={
                                changeStatusCategoryLoading &&
                                selectedCategory.id === category.id
                              }
                              className={`w-full text-left px-4 py-3 font-nunito text-[14px] ${
                                category.isActive
                                  ? "text-red-600"
                                  : "text-green-600"
                              } hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {changeStatusCategoryLoading &&
                              selectedCategory.id === category.id ? (
                                <>
                                  <div
                                    className={`animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 ${
                                      category.isActive
                                        ? "border-red-600"
                                        : "border-green-600"
                                    }`}
                                  ></div>
                                  <span>
                                    {category.isActive
                                      ? "Désactivation..."
                                      : "Activation..."}
                                  </span>
                                </>
                              ) : (
                                <span>
                                  {category.isActive ? "Désactiver" : "Activer"}
                                </span>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      {services.map((service) => (
                        <div
                          key={service.id}
                          className="flex items-center p-2 md:p-4 border border-l-4 border-l-secondary-300 border-gray-300 bg-white hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex-1 flex justify-between items-center">
                            {/* Informations du service */}
                            <div className="flex-1">
                              <div className="flex items-start gap-2 md:gap-3 mb-1">
                                <h3 className="font-poppins font-semibold text-[18px] text-title">
                                  {service.nom}
                                </h3>
                                <span
                                  className={`px-2 py-1 font-nunito text-[14px] font-medium ${
                                    service.is_active
                                      ? "bg-green-100 text-green-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  {service.is_active ? "Actif" : "Inactif"}
                                </span>
                              </div>

                              {/* <div className="flex items-center gap-4 font-nunito text-[14px] text-text">
                                {service.durationMinutes && (
                                  <span>{service.durationMinutes} min</span>
                                )}
                              </div> */}
                            </div>

                            {/* Prix et actions */}
                            <div className="flex flex-col md:flex-row justify-end items-end gap-2 md:gap-4 ml-2 md:ml-4">
                              <span className="font-poppins font-bold text-[16px] text-montant text-right">
                                {service.price} F CFA
                              </span>

                              {/* Menu d'actions */}
                              <div className="relative">
                                <button
                                  className="p-2 hover:bg-gray-200 transition-colors"
                                  onClick={() =>
                                    setActiveDropdown(
                                      activeDropdown === service.id
                                        ? null
                                        : service.id
                                    )
                                  }
                                >
                                  <BsThreeDotsVertical className="text-gray-600" />
                                </button>

                                {activeDropdown === service.id && (
                                  <div className="absolute right-0  mt-1 w-48 bg-white shadow-lg border border-gray-200 z-10">
                                    <button
                                      onClick={() => handleEditService(service)}
                                      className="w-full text-left px-4 py-3 font-nunito text-[14px] text-text hover:bg-gray-100"
                                    >
                                      Modifier
                                    </button>

                                    <button
                                      onClick={() =>
                                        handleToggleServiceStatus(service)
                                      }
                                      disabled={
                                        changeStatusServiceLoading &&
                                        selectedCategory.id === service.id
                                      }
                                      className={`w-full text-left px-4 py-3 font-nunito text-[14px] ${
                                        service.is_active
                                          ? "text-red-600"
                                          : "text-green-600"
                                      } hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                                    >
                                      {changeStatusServiceLoading &&
                                      selectedCategory.id === service.id ? (
                                        <>
                                          <div
                                            className={`animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 ${
                                              service.is_active
                                                ? "border-red-600"
                                                : "border-green-600"
                                            }`}
                                          ></div>
                                          <span>
                                            {service.is_active
                                              ? "Désactivation..."
                                              : "Activation..."}
                                          </span>
                                        </>
                                      ) : (
                                        <span>
                                          {service.is_active
                                            ? "Désactiver"
                                            : "Activer"}
                                        </span>
                                      )}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {selectedCategory === "all" &&
                      groupedServices.length > 1 && (
                        <div className="mt-6 border-t border-gray-200"></div>
                      )}
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="font-nunito text-[14px] text-text">
                    Aucune prestation trouvée avec les critères sélectionnés
                  </p>
                  {(filters.status || filters.search) && (
                    <button
                      onClick={resetFilters}
                      className="mt-2 font-nunito font-semibold text-[14px] text-action hover:text-secondary-700"
                    >
                      Réinitialiser les filtres
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AddCategoryModal
        isCategoryModalOpen={addCategoryModal}
        closeCategoryModal={() => setAddCategoryModal(false)}
      />
      <EditCategoryModal
        isEditCategoryModalOpen={isEditCategoryModal}
        closeEditCategoryModal={() => setIsEditCategoryModal(false)}
        selectedCategory={selectedActionCategory}
      />

      <AddServiceModal
        isAddModalOpen={addModal}
        closeAddModal={() => setAddModal(false)}
      />
      <EditServiceModal
        isEditModalOpen={isEditModal}
        closeEditModal={() => setIsEditModal(false)}
        selectedService={selectedService}
      />
    </div>
  );
};

export default CataloguePage;